import {
  Component,
  ChangeDetectionStrategy,
  input,
  forwardRef,
  signal,
  computed,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { DateTime } from "luxon";

/**
 * Unified DateTime picker component using native HTML5 inputs.
 *
 * Features:
 * - Native HTML5 input types for reliable validation and cross-platform support
 * - Always accepts and emits ISO datetime strings in UTC (with Z suffix)
 * - Displays values in local timezone, outputs in UTC
 * - Mobile-friendly with native OS pickers
 *
 * Modes:
 * - 'datetime': input type="datetime-local"
 * - 'date': input type="date"
 * - 'time': input type="time"
 */
@Component({
  selector: "app-datetime-picker",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { style: "display: block" },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimePickerComponent),
      multi: true,
    },
  ],
  template: `
    <input
      [type]="inputType()"
      class="form-control"
      [class.is-invalid]="isInvalid()"
      [value]="nativeInputValue()"
      [disabled]="isDisabled()"
      (change)="onInputChange($event)"
      (blur)="markAsTouched()"
    />
  `,
})
export class DateTimePickerComponent implements ControlValueAccessor {
  // Inputs
  mode = input<"datetime" | "date" | "time">("datetime");
  isInvalid = input<boolean>(false);

  // State signals
  isDisabled = signal(false);
  private currentDateTime = signal<DateTime | null>(null);

  // CVA callbacks (noop defaults, overwritten by registerOnChange/registerOnTouched)
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  // Computed: Input type based on mode
  inputType = computed(() => {
    switch (this.mode()) {
      case "time":
        return "time";
      case "date":
        return "date";
      default:
        return "datetime-local";
    }
  });

  // Computed: Value formatted for native input
  nativeInputValue = computed(() => {
    const dt = this.currentDateTime();
    if (!dt?.isValid) return "";

    switch (this.mode()) {
      case "time":
        return dt.toFormat("HH:mm");
      case "date":
        return dt.toFormat("yyyy-MM-dd");
      default:
        return dt.toFormat("yyyy-MM-dd'T'HH:mm");
    }
  });

  // ========== Public Methods ==========

  markAsTouched(): void {
    this.onTouched();
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    if (!value) {
      this.updateValue(null);
      return;
    }

    const parsed = this.parseNativeInput(value);
    if (parsed) {
      this.updateValue(parsed);
    }
  }

  // ========== ControlValueAccessor Implementation ==========

  writeValue(isoString: string | null): void {
    if (!isoString) {
      this.currentDateTime.set(null);
      return;
    }

    if (this.mode() === "time") {
      // Time-only: parse as wall clock time (no timezone conversion)
      // Round times represent venue-local times, not UTC timestamps
      let dt = DateTime.fromISO(isoString);
      if (!dt.isValid) {
        // Fallback: try parsing as time-only string (e.g., "12:30" or "12:30:00")
        const timeStr =
          isoString.length >= 5 ? isoString.substring(0, 5) : isoString;
        dt = DateTime.fromFormat(timeStr, "HH:mm");
      }
      if (dt.isValid) {
        this.currentDateTime.set(dt);
      }
      return;
    }

    // Datetime/date: parse as UTC, convert to local for display
    const dt = DateTime.fromISO(isoString, { zone: "utc" }).toLocal();
    if (dt.isValid) {
      this.currentDateTime.set(dt);
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  // ========== Private Methods ==========

  private parseNativeInput(value: string): DateTime | null {
    const currentMode = this.mode();

    if (currentMode === "time") {
      const dt = DateTime.fromFormat(value, "HH:mm");
      if (!dt.isValid) return null;

      // Preserve original date, update time
      const original = this.currentDateTime();
      if (original) {
        return original.set({
          hour: dt.hour,
          minute: dt.minute,
          second: 0,
          millisecond: 0,
        });
      } else {
        return DateTime.now().set({
          hour: dt.hour,
          minute: dt.minute,
          second: 0,
          millisecond: 0,
        });
      }
    } else if (currentMode === "date") {
      const dt = DateTime.fromFormat(value, "yyyy-MM-dd", { zone: "utc" });
      return dt.isValid ? dt : null;
    } else {
      const dt = DateTime.fromFormat(value, "yyyy-MM-dd'T'HH:mm");
      return dt.isValid ? dt : null;
    }
  }

  private updateValue(dt: DateTime | null): void {
    if (!dt) {
      this.currentDateTime.set(null);
      this.onChange(null);
      this.onTouched();
      return;
    }

    this.currentDateTime.set(dt);

    if (this.mode() === "time") {
      // Time-only: emit as local ISO datetime (no UTC conversion)
      // Round times represent venue-local wall clock times, not UTC timestamps
      this.onChange(dt.toISO());
    } else {
      // Datetime/date: output UTC with Z suffix to match API expectations
      this.onChange(dt.toUTC().toISO());
    }
    this.onTouched();
  }
}
